import { useEffect, useRef, useState } from "react";
import { Navigate, Link, NavLink, Routes, Route, useParams, useNavigate } from "react-router-dom";
import { LayoutDashboard, Film, BarChart3, Upload, Settings, LogOut, Zap, ChevronLeft, Star, Trash2, Edit3, X, Link as LinkIcon, Activity, Users, Eye, Clock, Globe, Monitor, TrendingUp, PlayCircle, Check, Sparkles, Wand2, Radio, ShieldAlert, Link2 } from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import client, { resolveMedia } from "../lib/api";
import { useAuth } from "../lib/auth";
import CategorySelect from "../components/CategorySelect";
import SourcesPage from "./SourcesPage";
import AnalyticsTabbed from "./admin/AnalyticsTabbed";
import VictoriaPage from "./admin/VictoriaPage";
import VictoriaBubble from "../components/VictoriaBubble";
import UtmLinksModal from "../components/UtmLinksModal";

/* ---------- helpers ---------- */
const fmtNum = (n) => {
  if (n === null || n === undefined) return "—";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return String(Math.round(n));
};
const fmtDur = (sec) => {
  if (!sec) return "0s";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${s}s`;
  return `${s}s`;
};
const fmtDay = (d) => {
  const dt = new Date(d);
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const COLORS = ["#FF6B00", "#FFB800", "#FF3D00", "#FFC857", "#FF8A3D", "#7A4F00", "#FFAA66", "#5A3A00"];

/* ---------- Mirrors editor ---------- */
const MIRROR_PLATFORMS = [
  { key: "youtube",     label: "YouTube",     placeholder: "https://youtube.com/watch?v=..." },
  { key: "tiktok",      label: "TikTok",      placeholder: "https://tiktok.com/@.../video/..." },
  { key: "instagram",   label: "Instagram",   placeholder: "https://instagram.com/p/..." },
  { key: "deviantart",  label: "DeviantArt",  placeholder: "https://www.deviantart.com/..." },
  { key: "spotify",     label: "Spotify",     placeholder: "https://open.spotify.com/track/..." },
  { key: "apple_music", label: "Apple Music", placeholder: "https://music.apple.com/..." },
  { key: "soundcloud",  label: "SoundCloud",  placeholder: "https://soundcloud.com/..." },
];
function MirrorsEditor({ value, onChange, testidPrefix = "mirrors", autoDetectUrl }) {
  const m = value || {};
  const [detecting, setDetecting] = useState(false);
  const filled = MIRROR_PLATFORMS.filter((p) => (m[p.key] || "").trim().startsWith("https://")).length;
  const invalid = MIRROR_PLATFORMS.filter((p) => {
    const v = (m[p.key] || "").trim();
    return v && !v.startsWith("https://");
  }).length;
  const set = (k, v) => onChange({ ...m, [k]: v });
  const autoDetect = async () => {
    if (!autoDetectUrl) {
      toast.error("Paste a source URL above first");
      return;
    }
    setDetecting(true);
    try {
      const r = await client.post("/admin/detect-mirrors", { url: autoDetectUrl });
      const detected = r.data?.mirrors || {};
      const found = Object.keys(detected).length;
      if (!found) {
        toast.warning("No mirrors detected in bio/description");
        return;
      }
      // Merge: only fill platforms the user hasn't already entered
      const merged = { ...m };
      let added = 0;
      for (const [k, v] of Object.entries(detected)) {
        if (!(merged[k] || "").trim()) {
          merged[k] = v;
          added += 1;
        }
      }
      onChange(merged);
      toast.success(`Detected ${found} · added ${added} new`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-detect failed");
    } finally {
      setDetecting(false);
    }
  };
  return (
    <div data-testid={`${testidPrefix}-editor`} className="rounded-xl border border-white/10 p-3 space-y-2 bg-black/30">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-[#FFB800]">Platform Mirrors</span>
        <span className={`text-[10px] uppercase tracking-widest ${filled ? "text-emerald-400" : "text-amber-400"}`} data-testid={`${testidPrefix}-count`}>
          {filled ? `${filled}/${MIRROR_PLATFORMS.length} linked` : "none set"}
        </span>
      </div>
      <p className="text-[10px] text-zinc-500 leading-snug">
        Adds outbound links to the <code className="text-[#FFB800]">/v/&lt;slug&gt;</code> landing page.
        Each URL must start with <code className="text-[#FFB800]">https://</code> or it&apos;ll be dropped on save.
      </p>
      {autoDetectUrl !== undefined && (
        <button
          type="button"
          data-testid={`${testidPrefix}-auto-detect`}
          onClick={autoDetect}
          disabled={detecting || !autoDetectUrl}
          className="w-full h-9 rounded-lg bg-zinc-900/70 border border-[#FFB800]/40 text-[#FFB800] hover:bg-[#FFB800]/10 text-[10px] uppercase tracking-widest font-black disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {detecting ? "Scanning bio…" : "✨ Auto-detect from creator bio"}
        </button>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {MIRROR_PLATFORMS.map((p) => {
          const v = m[p.key] || "";
          const ok = !v || v.trim().startsWith("https://");
          return (
            <label key={p.key} className="block">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">{p.label}</span>
              <input
                data-testid={`${testidPrefix}-${p.key}`}
                value={v}
                onChange={(e) => set(p.key, e.target.value)}
                placeholder={p.placeholder}
                className={`mt-1 w-full h-9 px-3 bg-zinc-900/70 border rounded-lg text-white text-xs font-mono outline-none ${ok ? "border-white/10 focus:border-[#FF6B00]" : "border-red-500/60"}`}
              />
            </label>
          );
        })}
      </div>
      {invalid > 0 && (
        <p className="text-[10px] text-red-400" data-testid={`${testidPrefix}-invalid-warning`}>
          {invalid} URL{invalid > 1 ? "s" : ""} must start with <code>https://</code> or will be dropped.
        </p>
      )}
      {filled === 0 && (
        <p className="text-[10px] text-amber-400" data-testid={`${testidPrefix}-empty-warning`}>
          ⚠ No mirrors set — the landing page won&apos;t link out to YouTube/TikTok/etc. and you&apos;ll lose the SEO loop. Add at least one to close it.
        </p>
      )}
    </div>
  );
}

/* ---------- Layout / Sidebar ---------- */
function StudioShell({ children }) {
  const { admin, logout } = useAuth();
  return (
    <div className="min-h-screen bg-[#050505] flex" data-testid="studio-shell">
      <aside className="hidden md:flex w-64 flex-col border-r border-white/5 bg-[#0a0a0a] sticky top-0 h-screen">
        <Link to="/admin" className="flex items-center gap-2 px-5 h-16 border-b border-white/5">
          <div className="relative">
            <Zap className="w-6 h-6 text-[#FF6B00]" strokeWidth={2.5} />
            <div className="absolute inset-0 blur-md bg-[#FF6B00]/40 rounded-full -z-10" />
          </div>
          <div className="flex flex-col leading-none">
            <span className="text-[9px] uppercase tracking-[0.3em] text-[#FFB800] font-bold">Saiyan</span>
            <span className="text-base font-black tracking-tight text-white">STUDIO</span>
          </div>
        </Link>

        <nav className="flex-1 py-6 px-3 space-y-1">
          <SideLink to="/admin" exact icon={LayoutDashboard} label="Dashboard" testid="side-dashboard" />
          <SideLink to="/admin/content" icon={Film} label="Content" testid="side-content" />
          <SideLink to="/admin/sources" icon={Radio} label="Sources" testid="side-sources" />
          <SideLink to="/admin/analytics" icon={BarChart3} label="Analytics" testid="side-analytics" />
          <SideLink to="/admin/victoria" icon={Sparkles} label="Victoria" testid="side-victoria" />
          <SideLink to="/admin/upload" icon={Upload} label="Upload" testid="side-upload" />
          <SideLink to="/admin/settings" icon={Settings} label="Settings" testid="side-settings" />
        </nav>

        <div className="px-3 py-4 border-t border-white/5">
          <Link to="/" className="flex items-center gap-2 px-3 h-10 rounded-lg text-zinc-400 hover:text-white text-xs uppercase tracking-widest" data-testid="exit-to-site">
            <ChevronLeft className="w-4 h-4" /> Back to Nexus
          </Link>
          <div className="mt-3 px-3 py-3 rounded-lg bg-zinc-900/60 border border-white/5">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500">Logged in</p>
            <p className="text-xs text-[#FFB800] font-mono truncate">{admin?.email}</p>
            <button onClick={logout} data-testid="studio-logout" className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-400 hover:text-red-400">
              <LogOut className="w-3 h-3" /> Logout
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        {/* Mobile top nav */}
        <div className="md:hidden flex items-center gap-2 px-3 py-3 border-b border-white/5 overflow-x-auto scrollbar-hide bg-[#0a0a0a] sticky top-0 z-30">
          <MobileLink to="/admin" exact label="Dashboard" />
          <MobileLink to="/admin/content" label="Content" />
          <MobileLink to="/admin/sources" label="Sources" />
          <MobileLink to="/admin/analytics" label="Analytics" />
          <MobileLink to="/admin/victoria" label="Victoria" />
          <MobileLink to="/admin/upload" label="Upload" />
          <MobileLink to="/admin/settings" label="Settings" />
        </div>
        <div className="px-4 sm:px-6 lg:px-10 py-8">{children}</div>
      </main>
    </div>
  );
}

function SideLink({ to, exact, icon: Icon, label, testid }) {
  return (
    <NavLink
      to={to}
      end={exact}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 h-11 rounded-lg text-sm font-medium transition-colors ${
          isActive
            ? "bg-[#FF6B00]/15 text-[#FFB800] border border-[#FF6B00]/30"
            : "text-zinc-400 hover:text-white hover:bg-white/5 border border-transparent"
        }`
      }
    >
      <Icon className="w-4 h-4" /> {label}
    </NavLink>
  );
}
function MobileLink({ to, exact, label }) {
  return (
    <NavLink
      to={to}
      end={exact}
      className={({ isActive }) =>
        `flex-shrink-0 px-3 h-9 rounded-full text-xs uppercase tracking-widest border ${
          isActive ? "bg-[#FF6B00] text-black font-bold border-[#FF6B00]" : "text-zinc-400 border-white/10"
        }`
      }
    >{label}</NavLink>
  );
}

/* ---------- Range selector ---------- */
function RangeSelect({ value, onChange }) {
  const opts = [{ k: 7, l: "7D" }, { k: 28, l: "28D" }, { k: 60, l: "60D" }, { k: 90, l: "90D" }];
  return (
    <div className="inline-flex p-1 rounded-full bg-zinc-900/60 border border-white/10" data-testid="range-select">
      {opts.map((o) => (
        <button
          key={o.k}
          onClick={() => onChange(o.k)}
          data-testid={`range-${o.k}`}
          className={`px-4 h-8 rounded-full text-xs uppercase tracking-widest ${
            value === o.k ? "bg-[#FF6B00] text-black font-bold" : "text-zinc-400 hover:text-white"
          }`}
        >{o.l}</button>
      ))}
    </div>
  );
}

/* ---------- KPI Tile ---------- */
function Kpi({ label, value, sub, icon: Icon, accent }) {
  return (
    <div
      data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}
      className={`relative overflow-hidden rounded-2xl p-5 border ${accent ? "border-[#FF6B00]/40 bg-gradient-to-br from-[#FF6B00]/10 to-transparent" : "border-white/5 bg-zinc-900/40"}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">{label}</p>
          <p className="mt-2 text-3xl font-black text-white tracking-tight">{value}</p>
          {sub && <p className="mt-1 text-xs text-zinc-500">{sub}</p>}
        </div>
        {Icon && (
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent ? "bg-[#FF6B00]/20 text-[#FFB800]" : "bg-white/5 text-zinc-400"}`}>
            <Icon className="w-5 h-5" />
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Charts ---------- */
function ViewsChart({ data }) {
  return (
    <div className="rounded-2xl p-5 border border-white/5 bg-zinc-900/40" data-testid="views-chart">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#FFB800] font-bold">Performance</p>
          <h3 className="text-lg font-bold text-white">Views & Watch Hours</h3>
        </div>
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest">
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#FF6B00]" /> Views</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#FFB800]" /> Hours</span>
        </div>
      </div>
      <div className="h-72 min-h-[18rem] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data}>
            <defs>
              <linearGradient id="gV" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#FF6B00" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#FF6B00" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gW" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#FFB800" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#FFB800" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
            <XAxis dataKey="day" tickFormatter={fmtDay} stroke="#5a5a5a" fontSize={11} tickLine={false} axisLine={false} />
            <YAxis stroke="#5a5a5a" fontSize={11} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,107,0,0.4)", borderRadius: 8 }} labelStyle={{ color: "#FFB800" }} />
            <Area type="monotone" dataKey="views" stroke="#FF6B00" strokeWidth={2} fill="url(#gV)" />
            <Area type="monotone" dataKey="watch_hours" stroke="#FFB800" strokeWidth={2} fill="url(#gW)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function RegionList({ regions }) {
  const max = Math.max(...regions.map((r) => r.watch_seconds), 1);
  return (
    <div className="rounded-2xl p-5 border border-white/5 bg-zinc-900/40" data-testid="region-list">
      <div className="flex items-center gap-2">
        <Globe className="w-4 h-4 text-[#FFB800]" />
        <h3 className="text-sm uppercase tracking-widest text-white font-bold">Top Regions</h3>
      </div>
      <div className="mt-4 space-y-3">
        {regions.length === 0 && <p className="text-xs text-zinc-500">No data yet.</p>}
        {regions.map((r) => (
          <div key={r.region}>
            <div className="flex items-center justify-between text-xs">
              <span className="text-white font-medium">{r.region || "Unknown"}</span>
              <span className="text-zinc-500 font-mono">{r.watch_hours}h · {fmtNum(r.views)} views</span>
            </div>
            <div className="mt-1.5 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#FF6B00] to-[#FFB800]"
                style={{ width: `${(r.watch_seconds / max) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DevicePie({ devices }) {
  const data = devices.map((d) => ({ name: d.device || "Unknown", value: d.watch_seconds || d.views || 1 }));
  return (
    <div className="rounded-2xl p-5 border border-white/5 bg-zinc-900/40" data-testid="device-pie">
      <div className="flex items-center gap-2">
        <Monitor className="w-4 h-4 text-[#FFB800]" />
        <h3 className="text-sm uppercase tracking-widest text-white font-bold">Devices</h3>
      </div>
      <div className="h-56 min-h-[14rem] mt-2 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" innerRadius={45} outerRadius={75} paddingAngle={2} stroke="none">
              {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,107,0,0.4)", borderRadius: 8 }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
        {devices.map((d, i) => (
          <div key={d.device} className="flex items-center justify-between bg-black/30 rounded-lg px-3 py-2">
            <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{d.device}</span>
            <span className="text-zinc-500 font-mono">{d.watch_hours}h</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TopVideos({ videos }) {
  return (
    <div className="rounded-2xl p-5 border border-white/5 bg-zinc-900/40" data-testid="top-videos">
      <div className="flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-[#FFB800]" />
        <h3 className="text-sm uppercase tracking-widest text-white font-bold">Top Performing</h3>
      </div>
      <div className="mt-4 space-y-2">
        {videos.length === 0 && <p className="text-xs text-zinc-500">Nothing tracked yet — uploads will appear here as they earn views.</p>}
        {videos.map((v, i) => (
          <Link
            key={v.id}
            to={`/admin/analytics/${v.id}`}
            data-testid={`top-video-${v.id}`}
            className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 transition-colors group"
          >
            <span className="text-zinc-600 text-xs font-mono w-5">{String(i + 1).padStart(2, "0")}</span>
            <div className="w-20 h-12 rounded-lg overflow-hidden bg-zinc-800 flex-shrink-0">
              {v.thumbnail && <img src={resolveMedia(v.thumbnail)} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-white truncate group-hover:text-[#FFB800]">{v.title}</p>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500">{v.type}</p>
            </div>
            <div className="text-right text-xs">
              <p className="text-white font-mono">{v.watch_hours}h</p>
              <p className="text-zinc-500 font-mono">{fmtNum(v.views_period)}v</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

/* ---------- Pages ---------- */
function Dashboard() {
  const [days, setDays] = useState(28);
  const [data, setData] = useState(null);

  useEffect(() => {
    client.get(`/admin/analytics/overview?days=${days}`).then((r) => setData(r.data));
  }, [days]);

  if (!data) return <div className="text-zinc-500" data-testid="dashboard-loading">Loading channel pulse...</div>;

  const k = data.kpis;
  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800]">Channel Pulse</span>
          <h1 className="text-3xl sm:text-4xl font-black text-white aura-text-glow tracking-tight">Mission Control</h1>
          <p className="mt-1 text-zinc-500 text-sm">Last <span className="text-[#FFB800]">{days}</span> days · live signal</p>
        </div>
        <RangeSelect value={days} onChange={setDays} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Kpi label="Views" value={fmtNum(k.views_period)} sub="period" icon={Eye} accent />
        <Kpi label="Watch Hours" value={fmtNum(k.watch_hours_period)} sub="period" icon={Clock} />
        <Kpi label="Avg. Duration" value={fmtDur(k.avg_view_duration_seconds)} sub="per view" icon={Activity} />
        <Kpi label="Total Videos" value={fmtNum(k.total_videos)} sub="library" icon={Film} />
        <Kpi label="All Time Views" value={fmtNum(k.total_views_all_time)} sub="lifetime" icon={Users} />
      </div>

      <ViewsChart data={data.timeseries} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2"><TopVideos videos={data.top_videos} /></div>
        <DevicePie devices={data.devices.length ? data.devices : [{ device: "—", watch_seconds: 1, watch_hours: 0 }]} />
      </div>
      <RegionList regions={data.regions} />
    </div>
  );
}

function RatingChip({ rating }) {
  const r = rating || "G";
  if (r === "G") return null;
  const cls =
    r === "Explicit" ? "bg-red-500/20 text-red-300 border-red-500/40" :
    r === "R"        ? "bg-orange-500/20 text-orange-300 border-orange-500/40" :
    r === "PG-13"    ? "bg-yellow-500/20 text-yellow-300 border-yellow-500/40" :
                       "bg-zinc-500/20 text-zinc-300 border-zinc-500/40";
  return (
    <span
      data-testid={`row-rating-${r}`}
      className={`inline-flex items-center h-5 px-1.5 rounded text-[9px] font-black uppercase tracking-widest border ${cls}`}
    >{r}</span>
  );
}


function BulkRatingPanel({ videos, onClose, onApplied }) {
  const cats = Array.from(new Set(videos.map((v) => v.category).filter(Boolean))).sort();
  const [selCats, setSelCats] = useState([]);
  const [selType, setSelType] = useState("");
  const [rating, setRating] = useState("R");
  const [applying, setApplying] = useState(false);

  // Preview of matching videos based on selection
  const matches = videos.filter((v) => {
    if (selType && v.type !== selType) return false;
    if (selCats.length && !selCats.includes(v.category)) return false;
    return true;
  });

  const toggle = (c) => setSelCats((s) => s.includes(c) ? s.filter((x) => x !== c) : [...s, c]);

  const apply = async () => {
    if (matches.length === 0) return;
    const confirmMsg = `Mark ${matches.length} video(s) as ${rating}?\n\nR / Explicit videos are hidden from public feeds and require age verification to watch.`;
    if (!window.confirm(confirmMsg)) return;
    setApplying(true);
    try {
      await client.post("/admin/videos/bulk-rating", {
        video_ids: matches.map((v) => v.id),
        age_rating: rating,
      });
      toast.success(`${matches.length} video(s) marked ${rating}.`);
      onApplied?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk rating failed");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div
      className="rounded-2xl border border-red-500/30 bg-gradient-to-b from-red-950/20 to-black/40 p-5 space-y-4"
      data-testid="bulk-rating-panel"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-white font-black">Bulk Age Rating</h3>
            <p className="text-xs text-zinc-400 mt-0.5">
              Filter by category and/or type, then apply a rating to all matches at once.
              R / Explicit will hide those videos from public feeds and require age verification.
            </p>
          </div>
        </div>
        <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="bulk-rating-close">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500">Categories</label>
          <div className="mt-2 flex flex-wrap gap-1.5 max-h-40 overflow-y-auto pr-1">
            {cats.length === 0 && <span className="text-xs text-zinc-500">No categories yet.</span>}
            {cats.map((c) => (
              <button
                key={c}
                onClick={() => toggle(c)}
                data-testid={`bulk-cat-${c}`}
                className={`h-8 px-3 rounded-full text-[10px] font-bold uppercase tracking-widest border ${
                  selCats.includes(c)
                    ? "bg-[#FF6B00] text-black border-[#FF6B00]"
                    : "bg-zinc-900 text-zinc-400 border-white/10"
                }`}
              >{c}</button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500">Type</label>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {[["", "Any"], ["long", "Video"], ["short", "Short"], ["music", "Music"], ["story", "Story"]].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setSelType(k)}
                data-testid={`bulk-type-${k || "any"}`}
                className={`h-8 px-3 rounded-full text-[10px] font-bold uppercase tracking-widest border ${
                  selType === k ? "bg-[#FF6B00] text-black border-[#FF6B00]" : "bg-zinc-900 text-zinc-400 border-white/10"
                }`}
              >{l}</button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500">Apply Rating</label>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {["G", "PG", "PG-13", "R", "Explicit"].map((r) => {
              const selected = rating === r;
              const isGated = r === "R" || r === "Explicit";
              return (
                <button
                  key={r}
                  onClick={() => setRating(r)}
                  data-testid={`bulk-rating-${r}`}
                  className={`h-9 px-3 rounded-full text-xs font-black uppercase tracking-widest border ${
                    selected
                      ? isGated
                        ? "bg-red-500/20 text-red-300 border-red-500/60"
                        : "bg-[#FF6B00] text-black border-[#FF6B00]"
                      : "bg-zinc-900 text-zinc-400 border-white/10"
                  }`}
                >{r}</button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-white/5">
        <p className="text-xs text-zinc-400" data-testid="bulk-rating-match-count">
          <span className="text-[#FFB800] font-black">{matches.length}</span> video(s) will be marked <span className="text-[#FFB800] font-black">{rating}</span>
          {matches.length > 0 && matches.length <= 8 && (
            <span className="text-zinc-500"> — {matches.slice(0, 8).map((m) => m.title).join(", ")}</span>
          )}
        </p>
        <button
          onClick={apply}
          disabled={applying || matches.length === 0}
          data-testid="bulk-rating-apply"
          className="h-10 px-5 rounded-full bg-red-500 hover:bg-red-500/90 text-white font-black uppercase tracking-widest text-xs disabled:opacity-40"
        >
          {applying ? "Applying..." : "Apply Rating"}
        </button>
      </div>
    </div>
  );
}


function ContentPage() {
  const [videos, setVideos] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [bulkOpen, setBulkOpen] = useState(false);
  // videoId (string) when a per-video modal is open, "home" for the global
  // homepage bio links modal, null when closed.
  const [utmOpen, setUtmOpen] = useState(null);

  const refresh = async () => {
    try {
      setLoading(true);
      const r = await client.get("/admin/videos");
      setVideos(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load content");
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    // Defer refresh() one tick so the set-state-in-effect rule doesn't flag.
    const t = setTimeout(refresh, 0);
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => {
      clearTimeout(t);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const filtered = filter === "all" ? videos : videos.filter((v) => v.type === filter);

  const del = async (v) => {
    if (!window.confirm(`Delete "${v.title}"?`)) return;
    await client.delete(`/admin/videos/${v.id}`);
    toast.success("Deleted.");
    refresh();
  };
  const toggleFeatured = async (v) => {
    await client.put(`/admin/videos/${v.id}`, { is_featured: !v.is_featured });
    refresh();
  };

  return (
    <div data-testid="content-page" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800]">Library</span>
          <h1 className="text-3xl font-black text-white tracking-tight">Your Content</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {[{ k: "all", l: "All" }, { k: "long", l: "Videos" }, { k: "short", l: "Shorts" }, { k: "music", l: "Music" }, { k: "story", l: "Stories" }].map((f) => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              data-testid={`content-filter-${f.k}`}
              className={`text-xs uppercase tracking-widest px-4 h-9 rounded-full border ${
                filter === f.k ? "bg-[#FF6B00] text-black border-[#FF6B00] font-bold" : "bg-transparent text-zinc-400 border-white/10 hover:border-white/30 hover:text-white"
              }`}
            >{f.l}</button>
          ))}
          <Link to="/admin/upload" data-testid="content-upload-cta" className="aura-button inline-flex items-center gap-2 h-9 px-4 rounded-full text-xs uppercase tracking-widest">
            <Upload className="w-3.5 h-3.5" /> New Upload
          </Link>
          <button
            data-testid="content-bio-links"
            onClick={() => setUtmOpen("home")}
            className="inline-flex items-center gap-2 h-9 px-4 rounded-full text-xs uppercase tracking-widest border border-[#FFB800]/40 text-[#FFB800] hover:bg-[#FFB800]/10"
          >
            <Link2 className="w-3.5 h-3.5" /> Bio Links
          </button>
          <button
            data-testid="content-bulk-rating-toggle"
            onClick={() => setBulkOpen((v) => !v)}
            className="inline-flex items-center gap-2 h-9 px-4 rounded-full text-xs uppercase tracking-widest border border-red-500/40 text-red-300 hover:bg-red-500/10"
          >
            <ShieldAlert className="w-3.5 h-3.5" /> Bulk Rating
          </button>
        </div>
      </div>

      {bulkOpen && (
        <BulkRatingPanel videos={videos} onClose={() => setBulkOpen(false)} onApplied={refresh} />
      )}

      <div className="rounded-2xl border border-white/5 bg-zinc-900/40 overflow-x-auto -mx-4 sm:mx-0" data-testid="content-table-wrap">
        <table className="w-full min-w-[520px] text-sm">
          <thead className="bg-black/30">
            <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
              <th className="p-4">Video</th>
              <th className="p-4 hidden sm:table-cell">Type</th>
              <th className="p-4 hidden md:table-cell">Category</th>
              <th className="p-4 hidden md:table-cell">Views</th>
              <th className="p-4 hidden lg:table-cell">Watch</th>
              <th className="p-4 hidden lg:table-cell">Created</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="p-12 text-center text-zinc-500">No content. <Link to="/admin/upload" className="text-[#FFB800] underline">Upload your first transmission</Link>.</td></tr>
            )}
            {loading && (
              <tr><td colSpan={7} className="p-12 text-center text-zinc-500">Loading library...</td></tr>
            )}
            {filtered.map((v) => (
              <tr key={v.id} className="border-t border-white/5 hover:bg-white/5 transition-colors">
                <td className="p-4">
                  <Link to={`/admin/analytics/${v.id}`} className="flex items-center gap-3 group" data-testid={`content-row-${v.id}`}>
                    <div className="w-20 h-12 rounded-lg bg-zinc-800 overflow-hidden flex-shrink-0">
                      {(v.thumbnail_path || v.thumbnail_url) && (
                        <img src={resolveMedia(v.thumbnail_path ? `/api/files/${v.thumbnail_path}` : v.thumbnail_url)} alt="" className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="min-w-0 max-w-[220px] sm:max-w-[280px] md:max-w-[320px] lg:max-w-[340px]">
                      <p className="font-bold text-white truncate group-hover:text-[#FFB800] transition-colors">{v.title}</p>
                      <p className="text-[10px] text-zinc-500 font-mono truncate">{v.id.slice(0, 8)} · {v.is_featured ? "★ featured" : "draft-public"}</p>
                    </div>
                  </Link>
                </td>
                <td className="p-4 hidden sm:table-cell text-zinc-400 uppercase text-xs">{v.type}</td>
                <td className="p-4 hidden md:table-cell text-zinc-400">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span>{v.category}</span>
                    <RatingChip rating={v.age_rating} />
                  </div>
                </td>
                <td className="p-4 hidden md:table-cell text-white font-mono">{fmtNum(v.views || 0)}</td>
                <td className="p-4 hidden lg:table-cell text-white font-mono">{fmtDur(v.watch_seconds || 0)}</td>
                <td className="p-4 hidden lg:table-cell text-zinc-500 text-xs">{new Date(v.created_at).toLocaleDateString()}</td>
                <td className="p-4 text-right whitespace-nowrap">
                  <div className="inline-flex items-center gap-2">
                    <Link to={`/admin/analytics/${v.id}`} className="w-9 h-9 rounded-full border border-white/10 text-zinc-400 hover:text-[#FFB800] flex items-center justify-center" data-testid={`stats-${v.id}`} title="Analytics"><BarChart3 className="w-4 h-4" /></Link>
                    <button data-testid={`feature-${v.id}`} onClick={() => toggleFeatured(v)} title="Feature" className={`w-9 h-9 rounded-full border flex items-center justify-center ${v.is_featured ? "border-[#FFB800] text-[#FFB800] bg-[#FFB800]/10" : "border-white/10 text-zinc-500 hover:text-white"}`}>
                      <Star className="w-4 h-4" fill={v.is_featured ? "currentColor" : "none"} />
                    </button>
                    <button data-testid={`edit-${v.id}`} onClick={() => setEditing(v)} className="w-9 h-9 rounded-full border border-white/10 text-zinc-400 hover:text-white flex items-center justify-center"><Edit3 className="w-4 h-4" /></button>
                    <button data-testid={`utm-${v.id}`} onClick={() => setUtmOpen(v.id)} title="UTM Links" className="w-9 h-9 rounded-full border border-[#FFB800]/30 text-[#FFB800] hover:bg-[#FFB800]/10 flex items-center justify-center"><Link2 className="w-4 h-4" /></button>
                    <button data-testid={`delete-${v.id}`} onClick={() => del(v)} className="w-9 h-9 rounded-full border border-red-500/30 text-red-400 hover:bg-red-500/10 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <EditModal video={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
      {utmOpen && (
        <UtmLinksModal
          videoId={utmOpen === "home" ? null : utmOpen}
          onClose={() => setUtmOpen(null)}
        />
      )}
    </div>
  );
}

function AnalyticsLanding({ hideHeader = false }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(28);
  useEffect(() => { client.get(`/admin/analytics/overview?days=${days}`).then((r) => setData(r.data)); }, [days]);
  if (!data) return <div className="text-zinc-500" data-testid="analytics-loading">Crunching numbers...</div>;
  const k = data.kpis;
  return (
    <div data-testid="analytics-page" className="space-y-6">
      {!hideHeader && (
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800]">Analytics</span>
            <h1 className="text-3xl font-black text-white tracking-tight">Channel Insights</h1>
          </div>
          <RangeSelect value={days} onChange={setDays} />
        </div>
      )}
      {hideHeader && (
        <div className="flex justify-end"><RangeSelect value={days} onChange={setDays} /></div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Views" value={fmtNum(k.views_period)} icon={Eye} accent />
        <Kpi label="Watch Hours" value={fmtNum(k.watch_hours_period)} icon={Clock} />
        <Kpi label="Avg. Duration" value={fmtDur(k.avg_view_duration_seconds)} icon={Activity} />
        <Kpi label="Library" value={fmtNum(k.total_videos)} icon={Film} />
      </div>
      <ViewsChart data={data.timeseries} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RegionList regions={data.regions} />
        <DevicePie devices={data.devices.length ? data.devices : [{ device: "—", watch_seconds: 1, watch_hours: 0 }]} />
      </div>
      <TopVideos videos={data.top_videos} />
    </div>
  );
}

function VideoAnalytics() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(28);
  useEffect(() => { client.get(`/admin/analytics/videos/${id}?days=${days}`).then((r) => setData(r.data)); }, [id, days]);
  if (!data) return <div className="text-zinc-500" data-testid="video-analytics-loading">Loading...</div>;
  const k = data.kpis;
  const v = data.video;
  return (
    <div data-testid="video-analytics-page" className="space-y-6">
      <Link to="/admin/content" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-zinc-400 hover:text-white" data-testid="back-content"><ChevronLeft className="w-4 h-4" /> All content</Link>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-start gap-4 max-w-3xl">
          <div className="w-32 h-20 rounded-lg overflow-hidden bg-zinc-800 flex-shrink-0">
            {v.thumbnail && <img src={resolveMedia(v.thumbnail)} alt="" className="w-full h-full object-cover" />}
          </div>
          <div>
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800]">{v.type} · {v.category}</span>
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight leading-tight">{v.title}</h1>
            <Link to={`/watch/${v.id}`} className="mt-1 inline-flex items-center gap-1 text-xs text-[#FFB800] uppercase tracking-widest hover:underline" data-testid="open-watch">
              <PlayCircle className="w-3.5 h-3.5" /> Open in player
            </Link>
          </div>
        </div>
        <RangeSelect value={days} onChange={setDays} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Kpi label="Views" value={fmtNum(k.total_views)} icon={Eye} accent />
        <Kpi label="Sessions" value={fmtNum(k.unique_sessions)} icon={Users} />
        <Kpi label="Watch Hours" value={fmtNum(k.total_watch_hours)} icon={Clock} />
        <Kpi label="Avg. Duration" value={fmtDur(k.avg_view_duration_seconds)} icon={Activity} />
        <Kpi label="Completions" value={fmtNum(k.completions)} icon={Check} />
      </div>

      <ViewsChart data={data.timeseries} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RegionList regions={data.regions} />
        <DevicePie devices={data.devices.length ? data.devices : [{ device: "—", watch_seconds: 1, watch_hours: 0 }]} />
      </div>

      {data.retention.length > 0 && (
        <div className="rounded-2xl p-5 border border-white/5 bg-zinc-900/40" data-testid="retention-chart">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#FFB800] font-bold">Audience Retention</p>
            <h3 className="text-lg font-bold text-white">% of viewers still watching</h3>
          </div>
          <div className="h-64 min-h-[16rem] mt-3 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.retention}>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
                <XAxis dataKey="label" stroke="#5a5a5a" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#5a5a5a" fontSize={11} tickLine={false} axisLine={false} unit="%" />
                <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,107,0,0.4)", borderRadius: 8 }} formatter={(v) => `${v}%`} />
                <Bar dataKey="pct_remaining" fill="#FF6B00" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Upload (kept tight) ---------- */
function UploadPage() {
  return (
    <div data-testid="upload-page" className="space-y-6">
      <div>
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800]">Studio</span>
        <h1 className="text-3xl font-black text-white tracking-tight">New Transmission</h1>
        <p className="mt-1 text-zinc-500 text-sm">Upload a long video, vertical short, or 24-hour story.</p>
      </div>
      <UploadForm />
    </div>
  );
}

function SettingsPage() {
  const { admin } = useAuth();
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const [vid, setVid] = useState("");
  const [autoplay, setAutoplay] = useState(true);
  const [muted, setMuted] = useState(true);
  const [transparent, setTransparent] = useState(false);
  const [fxOff, setFxOff] = useState(false);
  const [theme, setTheme] = useState("aura");
  const [nextOnEnd, setNextOnEnd] = useState(true);

  const qp = new URLSearchParams();
  qp.set("embed", "1");
  if (autoplay) qp.set("autoplay", "1");
  qp.set("muted", muted ? "1" : "0");
  if (transparent) qp.set("bg", "transparent");
  if (fxOff) qp.set("fx", "off");
  if (theme === "mono") qp.set("theme", "mono");
  if (nextOnEnd) qp.set("next", "1");
  const path = vid.trim() ? `/terminal/${vid.trim()}` : `/terminal`;
  const embedUrl = `${origin}${path}?${qp.toString()}`;
  const iframe = `<iframe
  src="${embedUrl}"
  allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
  allowfullscreen
  style="width:100%;height:100%;border:0;background:${transparent ? "transparent" : "#000"};"
  loading="lazy"
></iframe>`;

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      const { toast } = await import("sonner");
      toast.success(`${label} copied`);
    } catch (_e) {
      // clipboard not available — silently ignore
    }
  };

  return (
    <div data-testid="settings-page" className="space-y-6 max-w-3xl">
      <div>
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800]">Studio</span>
        <h1 className="text-3xl font-black text-white tracking-tight">Settings</h1>
      </div>

      <div className="rounded-2xl p-6 border border-white/5 bg-zinc-900/40 space-y-4">
        <Field label="Admin Email" value={admin?.email} />
        <Field label="Role" value="Channel Owner" />
      </div>

      {/* IndexNow */}
      <IndexNowPanel />

      {/* Vanessa webhook */}
      <VanessaPanel />

      {/* Terminal Embed Builder */}
      <div className="rounded-2xl p-6 border border-[#FF6B00]/20 bg-zinc-900/40 space-y-5" data-testid="embed-builder">
        <div>
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800]">Saiyan Terminal Embed</span>
          <h2 className="text-xl font-black text-white mt-1">Embed Snippet Builder</h2>
          <p className="text-xs text-zinc-500 mt-1">
            Paste the iframe into <span className="text-[#FFB800]">terminal.thesaiyanvictoria.com</span>. The player exposes a postMessage bridge (<code className="text-[#FFB800]">source: &quot;saiyan-nexus&quot;</code>) for play/pause/seek/load/next.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <label className="space-y-1">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">Video ID (blank = list mode)</span>
            <input
              data-testid="embed-vid"
              value={vid}
              onChange={(e) => setVid(e.target.value)}
              placeholder="e.g. 39edf827-77dc-4e27-9b68-dcda5ab8af6d"
              className="w-full bg-black border border-white/10 rounded-lg px-3 py-2 font-mono text-xs text-white focus:border-[#FF6B00]/60 outline-none"
            />
          </label>
          <label className="space-y-1">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">Theme</span>
            <select
              data-testid="embed-theme"
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
              className="w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-white focus:border-[#FF6B00]/60 outline-none"
            >
              <option value="aura">Aura (orange/gold)</option>
              <option value="mono">Mono (terminal grayscale)</option>
            </select>
          </label>
        </div>

        <div className="flex flex-wrap gap-3 text-xs text-zinc-300">
          <Toggle label="Autoplay" v={autoplay} set={setAutoplay} testid="embed-autoplay" />
          <Toggle label="Muted" v={muted} set={setMuted} testid="embed-muted" />
          <Toggle label="Transparent BG" v={transparent} set={setTransparent} testid="embed-transparent" />
          <Toggle label="Disable FX" v={fxOff} set={setFxOff} testid="embed-fxoff" />
          <Toggle label="Auto-advance" v={nextOnEnd} set={setNextOnEnd} testid="embed-next" />
        </div>

        <div className="space-y-2">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">Embed URL</span>
          <div className="flex gap-2">
            <input
              readOnly
              value={embedUrl}
              data-testid="embed-url-output"
              className="flex-1 font-mono text-xs bg-black border border-white/10 rounded-lg px-3 py-2 text-white"
            />
            <button
              onClick={() => copy(embedUrl, "URL")}
              data-testid="embed-copy-url"
              className="px-3 rounded-lg bg-[#FF6B00] text-black text-[10px] font-black uppercase tracking-widest hover:brightness-110"
            >
              Copy
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">iFrame Snippet</span>
          <pre
            data-testid="embed-iframe-output"
            className="font-mono text-[11px] text-zinc-300 bg-black border border-white/10 rounded-lg px-3 py-3 whitespace-pre-wrap break-all"
          >{iframe}</pre>
          <button
            onClick={() => copy(iframe, "iFrame")}
            data-testid="embed-copy-iframe"
            className="px-3 py-1.5 rounded-lg bg-[#FF6B00] text-black text-[10px] font-black uppercase tracking-widest hover:brightness-110"
          >
            Copy iFrame
          </button>
        </div>

        <details className="text-xs text-zinc-400">
          <summary className="cursor-pointer text-[#FFB800] uppercase tracking-widest text-[10px]">postMessage Bridge</summary>
          <pre className="mt-2 font-mono text-[11px] bg-black/60 border border-white/5 rounded-lg p-3 overflow-x-auto">{`// FROM your terminal page TO the embedded iframe:
iframe.contentWindow.postMessage({ target: "saiyan-nexus", type: "play" }, "*");
iframe.contentWindow.postMessage({ target: "saiyan-nexus", type: "pause" }, "*");
iframe.contentWindow.postMessage({ target: "saiyan-nexus", type: "toggle" }, "*");
iframe.contentWindow.postMessage({ target: "saiyan-nexus", type: "seek", payload: { t: 30 } }, "*");
iframe.contentWindow.postMessage({ target: "saiyan-nexus", type: "mute", payload: { muted: true } }, "*");
iframe.contentWindow.postMessage({ target: "saiyan-nexus", type: "volume", payload: { value: 0.6 } }, "*");
iframe.contentWindow.postMessage({ target: "saiyan-nexus", type: "load", payload: { id: "<video-id>" } }, "*");
iframe.contentWindow.postMessage({ target: "saiyan-nexus", type: "load", payload: { cat: "Action", type: "short" } }, "*");  // list-mode deep link
iframe.contentWindow.postMessage({ target: "saiyan-nexus", type: "filter", payload: { q: "Vanessa" } }, "*");                 // search-by-tag/title
iframe.contentWindow.postMessage({ target: "saiyan-nexus", type: "filter", payload: { cat: "", type: "", q: "" } }, "*");    // clear filters
iframe.contentWindow.postMessage({ target: "saiyan-nexus", type: "next" }, "*");

// FROM the iframe TO your terminal page:
window.addEventListener("message", (e) => {
  if (e.data?.source !== "saiyan-nexus") return;
  // e.data.type: "ready" | "loaded" | "play" | "pause" | "time" | "ended" | "error" | "select" | "filtered" | "navigate"
  // "navigate" -> reverse bridge: { payload:{ route:"/characters/<id>", character:"<id>" } }
  //   when a video plays whose title/tags mention a known SaiyanVictoria character.
  console.log("[nexus]", e.data.type, e.data.payload);
});`}</pre>
        </details>
      </div>
    </div>
  );
}

function Toggle({ label, v, set, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={() => set(!v)}
      className={`px-3 py-1.5 rounded-full border transition-colors ${v ? "bg-[#FF6B00] text-black border-[#FF6B00]" : "border-white/15 text-zinc-400 hover:text-white"}`}
    >
      {v ? "✓ " : ""}{label}
    </button>
  );
}

function VanessaPanel() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [logs, setLogs] = useState(null);
  const [totals, setTotals] = useState({ sent: 0, failed: 0, skipped: 0 });
  const [loadingLogs, setLoadingLogs] = useState(false);

  const loadLogs = async () => {
    setLoadingLogs(true);
    try {
      const r = await client.get("/admin/vanessa/logs?limit=50");
      setLogs(r.data.items || []);
      setTotals(r.data.totals || { sent: 0, failed: 0, skipped: 0 });
    } catch {
      setLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  useEffect(() => { const t = setTimeout(loadLogs, 0); return () => clearTimeout(t); }, []);

  const fire = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await client.post("/admin/vanessa/test");
      setResult(r.data);
      const { toast } = await import("sonner");
      if (r.data.sent) toast.success(`Vanessa: log ${r.data.log_id?.slice(0, 8) || ""}…`);
      else if (r.data.skipped) toast.message(r.data.reason || "Skipped");
      else toast.error(r.data.error || `HTTP ${r.data.status}`);
      loadLogs();
    } catch {
      const { toast } = await import("sonner");
      toast.error("Vanessa test failed");
    } finally {
      setBusy(false);
    }
  };

  const resubmit = async (videoId) => {
    try {
      const r = await client.post(`/admin/vanessa/resubmit/${videoId}`);
      const { toast } = await import("sonner");
      if (r.data.sent) toast.success("Resent ✓");
      else toast.error(r.data.error || r.data.reason || `HTTP ${r.data.status}`);
      loadLogs();
    } catch (_e) {
      // silent — UI shows error state via failed log entry
    }
  };

  const resubmitAll = async () => {
    if (!window.confirm("Resubmit every video to Vanessa? Idempotent for 1h on URL.")) return;
    try {
      const r = await client.post("/admin/vanessa/resubmit-all");
      const { toast } = await import("sonner");
      toast.success(`Backfill: ${r.data.sent} sent, ${r.data.failed} failed, ${r.data.skipped} skipped`);
      loadLogs();
    } catch (_e) {
      // silent — toast is the main feedback path
    }
  };

  const fmtTime = (iso) => {
    try {
      return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
      return iso;
    }
  };

  return (
    <div data-testid="vanessa-panel" className="rounded-2xl p-6 border border-[#76FFE1]/20 bg-zinc-900/40 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#76FFE1]">SaiyanVictoria Pipeline</span>
          <h2 className="text-xl font-black text-white mt-1">Vanessa Webhook</h2>
          <p className="text-xs text-zinc-500 mt-1">
            Every new video auto-pings <span className="text-[#76FFE1]">api.thesaiyanvictoria.com/api/vanessa/new-content</span> with the video URL, title, description, persona (auto-detected) and external_urls.
          </p>
        </div>
        <div className="flex gap-3 text-[10px] uppercase tracking-widest font-mono">
          <span data-testid="vanessa-totals-sent" className="text-[#76FFE1]">✓ {totals.sent} sent</span>
          <span data-testid="vanessa-totals-failed" className="text-red-400">✗ {totals.failed} failed</span>
          <span className="text-zinc-500">— {totals.skipped} skipped</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          data-testid="vanessa-test"
          onClick={fire}
          disabled={busy}
          className="px-3 py-2 rounded-lg bg-[#76FFE1] text-black text-[10px] font-black uppercase tracking-widest hover:brightness-110 disabled:opacity-50"
        >
          {busy ? "Pinging…" : "↗ Test (latest video)"}
        </button>
        <button
          data-testid="vanessa-refresh"
          onClick={loadLogs}
          disabled={loadingLogs}
          className="px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-white/10 text-white text-[10px] uppercase tracking-widest disabled:opacity-50"
        >
          ↻ Refresh
        </button>
        <button
          data-testid="vanessa-resubmit-all"
          onClick={resubmitAll}
          className="px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-[#76FFE1]/30 text-[#76FFE1] text-[10px] uppercase tracking-widest"
        >
          ⟲ Backfill all
        </button>
      </div>

      {result && (
        <pre data-testid="vanessa-result" className="font-mono text-[11px] bg-black/60 border border-white/5 rounded-lg p-3 text-zinc-300 whitespace-pre-wrap">
{JSON.stringify(result, null, 2)}
        </pre>
      )}

      {/* Activity log */}
      <div className="space-y-2" data-testid="vanessa-log-list">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Recent activity</div>
        {logs === null && <div className="text-xs text-zinc-500 font-mono">loading…</div>}
        {logs !== null && logs.length === 0 && (
          <div className="text-xs text-zinc-500 font-mono">no webhook activity yet — publish a video to trigger one</div>
        )}
        {logs && logs.length > 0 && (
          <ul className="divide-y divide-white/5 border border-white/5 rounded-lg overflow-hidden">
            {logs.map((l) => (
              <li key={l.id} data-testid={`vanessa-log-${l.id}`} className="px-3 py-2.5 flex items-center gap-3 text-xs hover:bg-white/[0.03]">
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${l.sent ? "bg-[#76FFE1]" : l.skipped ? "bg-zinc-500" : "bg-red-500"}`} />
                <span className="text-zinc-500 font-mono w-24 flex-shrink-0">{fmtTime(l.created_at)}</span>
                <span className="text-white flex-1 truncate" title={l.video_title}>{l.video_title || l.video_id || "—"}</span>
                {l.persona && <span className="hidden sm:inline text-[#FFB800] text-[10px] uppercase tracking-widest">{l.persona}</span>}
                {l.idempotency_hit && <span className="text-[10px] uppercase tracking-widest text-zinc-500" title="Vanessa de-duped this">dup</span>}
                {l.sent && l.vanessa_log_id && (
                  <span className="text-zinc-600 font-mono text-[10px] hidden md:inline" title="Vanessa log id">{l.vanessa_log_id.slice(0, 8)}</span>
                )}
                {!l.sent && !l.skipped && (
                  <span className="text-red-400 text-[10px] uppercase tracking-widest truncate max-w-[120px]" title={l.error || ""}>
                    {l.status ? `HTTP ${l.status}` : "error"}
                  </span>
                )}
                {l.skipped && <span className="text-zinc-500 text-[10px] uppercase tracking-widest">skipped</span>}
                {l.video_id && (
                  <button
                    data-testid={`vanessa-resubmit-${l.video_id}`}
                    onClick={() => resubmit(l.video_id)}
                    className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#76FFE1] flex-shrink-0"
                    title="Resubmit this video to Vanessa"
                  >
                    ⟲ resend
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


function IndexNowPanel() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await client.post("/admin/indexnow/resubmit-all");
      setResult(r.data);
      const { toast } = await import("sonner");
      toast.success(`IndexNow: submitted ${r.data.total_videos} URLs`);
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error("IndexNow submission failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="indexnow-panel" className="rounded-2xl p-6 border border-[#FFB800]/20 bg-zinc-900/40 space-y-4">
      <div>
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800]">Search Indexing</span>
        <h2 className="text-xl font-black text-white mt-1">IndexNow</h2>
        <p className="text-xs text-zinc-500 mt-1">
          New imports auto-ping <span className="text-[#FFB800]">api.indexnow.org</span> so Bing &amp; Yandex re-crawl your <code className="text-[#FFB800]">/watch/&lt;id&gt;</code> pages within minutes. Use the button below to bulk-resubmit the entire catalog (e.g., after switching to a custom domain).
        </p>
      </div>
      <button
        data-testid="indexnow-resubmit-all"
        onClick={submit}
        disabled={busy}
        className="px-4 py-2 rounded-lg bg-[#FFB800] text-black text-[10px] font-black uppercase tracking-widest hover:brightness-110 disabled:opacity-50"
      >
        {busy ? "Submitting…" : "↻ Resubmit All Videos"}
      </button>
      {result && (
        <pre data-testid="indexnow-result" className="font-mono text-[11px] bg-black/60 border border-white/5 rounded-lg p-3 text-zinc-300 whitespace-pre-wrap">
{JSON.stringify(result, null, 2)}
        </pre>
      )}
    </div>
  );
}
function Field({ label, value, mono }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</p>
      <p className={`mt-1 ${mono ? "font-mono text-sm" : "text-base"} text-white break-all`}>{value}</p>
    </div>
  );
}

/* ---------- Upload form (carried over, lightly polished) ---------- */
function UploadForm() {
  const [type, setType] = useState("long");
  const [sourceMode, setSourceMode] = useState("upload"); // upload | url | import
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("POV");
  const [tags, setTags] = useState("");
  const [series, setSeries] = useState("");
  const [seriesOrder, setSeriesOrder] = useState("");
  const [featured, setFeatured] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [thumbUrl, setThumbUrl] = useState("");
  const [videoFile, setVideoFile] = useState(null);
  const [thumbFile, setThumbFile] = useState(null);
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  // Vanessa landing-page fields
  const [slug, setSlug] = useState("");
  const [mirrors, setMirrors] = useState({});
  const [musicRelease, setMusicRelease] = useState("");
  // Import-from-URL state
  const [importUrl, setImportUrl] = useState("");
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const navigate = useNavigate();

  const reset = () => {
    setTitle(""); setDescription(""); setTags(""); setFeatured(false);
    setVideoUrl(""); setThumbUrl(""); setVideoFile(null); setThumbFile(null); setProgress(null);
    setSeries(""); setSeriesOrder("");
    setSlug(""); setMirrors({}); setMusicRelease("");
    setImportUrl(""); setImportPreview(null);
  };

  const fetchPreview = async () => {
    if (!importUrl.trim()) return toast.error("Paste a TikTok / Reels / Shorts / X URL");
    setImporting(true); setImportPreview(null);
    try {
      const r = await client.post("/admin/import/preview", { url: importUrl.trim() });
      setImportPreview(r.data);
      // Smart-fill the form
      if (!title.trim()) setTitle(r.data.title || "");
      if (!description.trim() && r.data.description) setDescription(r.data.description.slice(0, 500));
      if (r.data.suggested_type) setType(r.data.suggested_type);
      if (r.data.is_vertical) setCategory("POV");
      toast.success("Preview ready — review and import");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not extract from URL");
    } finally {
      setImporting(false);
    }
  };

  const runImport = async (e) => {
    e.preventDefault();
    if (!importUrl.trim()) return toast.error("Paste a URL first");
    setBusy(true);
    try {
      const r = await client.post("/admin/import", {
        url: importUrl.trim(),
        type,
        category: category.trim() || "General",
        tags: tags ? tags.split(",").map((s) => s.trim()).filter(Boolean) : null,
        is_featured: featured,
        series: series.trim() || null,
        series_order: seriesOrder ? parseInt(seriesOrder, 10) : null,
        title_override: title.trim() || null,
        description_override: description || null,
        slug: slug.trim() || null,
        mirrors,
        music_release: musicRelease.trim() || null,
      }, { timeout: 180000 });
      toast.success("Imported · transmission live.");
      reset();
      navigate(`/admin/analytics/${r.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const AUDIO_EXTS = ["mp3", "wav", "flac", "m4a", "aac", "ogg", "opus"];
  const isAudio = (file) => {
    if (!file) return false;
    if ((file.type || "").startsWith("audio/")) return true;
    const ext = (file.name || "").toLowerCase().split(".").pop();
    return AUDIO_EXTS.includes(ext);
  };
  const pickFile = (f) => {
    if (!f) return;
    setVideoFile(f);
    if (isAudio(f)) {
      // Switch the form into music mode so submit posts type=music + audio art
      setType("music");
      // Prefill title from filename only if user hasn't typed one yet
      if (!title.trim()) {
        const stripped = (f.name || "").replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim();
        if (stripped) setTitle(stripped);
      }
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    pickFile(e.dataTransfer.files?.[0]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      let video_path = null, video_url_final = null, thumbnail_path = null, thumbnail_url_final = null;
      let auto_meta = null;  // audio: ID3-extracted title/artist/album/embedded-art
      if (sourceMode === "upload") {
        if (!videoFile) throw new Error("Pick a video or audio file");
        const isBigFile = videoFile.size > 50 * 1024 * 1024;  // >50 MB → chunked
        if (!isBigFile) {
          // Small file: single-shot upload (faster, simpler)
          const fd = new FormData();
          fd.append("file", videoFile);
          fd.append("kind", isAudio(videoFile) ? "audio" : "video");
          const r = await client.post("/admin/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (ev) => setProgress(Math.round((ev.loaded / (ev.total || 1)) * 100)),
          });
          video_path = r.data.path;
          auto_meta = r.data.audio_meta || null;
          if (auto_meta?.thumbnail_path) thumbnail_path = auto_meta.thumbnail_path;
        } else {
          // Large file: chunked upload bypasses ingress client_max_body_size.
          // 8 MB chunks are small enough to clear any reasonable proxy cap
          // and big enough that even a 2 GB file only needs ~256 round trips.
          const CHUNK = 8 * 1024 * 1024;
          const init = await client.post("/admin/upload/chunked/init", {
            filename: videoFile.name,
            total_size: videoFile.size,
            kind: isAudio(videoFile) ? "audio" : "video",
          });
          const uploadId = init.data.upload_id;
          const totalChunks = Math.ceil(videoFile.size / CHUNK);
          for (let i = 0; i < totalChunks; i++) {
            const slice = videoFile.slice(i * CHUNK, Math.min((i + 1) * CHUNK, videoFile.size));
            const fd = new FormData();
            fd.append("upload_id", uploadId);
            fd.append("chunk_index", String(i));
            fd.append("chunk", slice);
            await client.post("/admin/upload/chunked/part", fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
            // Overall progress: assume 5% reserved for /complete, 95% for chunks
            setProgress(Math.round(((i + 1) / totalChunks) * 95));
          }
          const completeFd = new FormData();
          completeFd.append("upload_id", uploadId);
          const r = await client.post("/admin/upload/chunked/complete", completeFd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          setProgress(100);
          video_path = r.data.path;
          auto_meta = r.data.audio_meta || null;
          if (auto_meta?.thumbnail_path) thumbnail_path = auto_meta.thumbnail_path;
        }
      } else {
        if (!videoUrl.trim()) throw new Error("Paste a video URL");
        video_url_final = videoUrl.trim();
      }
      if (thumbFile) {
        const fd = new FormData();
        fd.append("file", thumbFile); fd.append("kind", "thumbnail");
        const r = await client.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        thumbnail_path = r.data.path;  // user-uploaded art overrides ID3-extracted
      } else if (thumbUrl.trim()) {
        thumbnail_url_final = thumbUrl.trim();
      }
      // For audio: surface the embedded artist/album info into tags + description if user left them blank
      let final_title = title.trim();
      let final_description = description;
      let final_tags = tags.split(",").map((s) => s.trim()).filter(Boolean);
      if (auto_meta) {
        if (auto_meta.title && (!final_title || final_title === videoFile.name?.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim())) {
          final_title = auto_meta.title;
        }
        if (auto_meta.artist && !final_tags.includes(auto_meta.artist)) {
          final_tags.unshift(auto_meta.artist);
        }
        if (auto_meta.album && !final_description) {
          final_description = `From the album: ${auto_meta.album}`;
        }
      }
      const created = await client.post("/admin/videos", {
        title: final_title, description: final_description, type, source_type: sourceMode,
        video_path, video_url: video_url_final, thumbnail_path, thumbnail_url: thumbnail_url_final,
        category: category.trim() || "General",
        tags: final_tags,
        is_featured: featured,
        series: series.trim() || null,
        series_order: seriesOrder ? parseInt(seriesOrder, 10) : null,
        slug: slug.trim() || null,
        mirrors,
        music_release: musicRelease.trim() || null,
        duration: auto_meta?.duration || null,
      });
      toast.success("Transmission published.");
      reset();
      navigate(`/admin/analytics/${created.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false); setProgress(null);
    }
  };

  const TYPES = [{ key: "long", label: "Long Video" }, { key: "short", label: "Short / Reel" }, { key: "story", label: "Story (24h)" }, { key: "music", label: "Music" }];

  return (
    <form onSubmit={sourceMode === "import" ? runImport : submit} className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="upload-form">
      <div className="lg:col-span-7 rounded-2xl p-6 border border-white/5 bg-zinc-900/40 space-y-5">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500">Type</label>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {TYPES.map((t) => (
              <button type="button" key={t.key} onClick={() => setType(t.key)} data-testid={`upload-type-${t.key}`}
                className={`h-12 rounded-lg border text-xs uppercase tracking-wider transition-colors ${type === t.key ? "border-[#FF6B00] bg-[#FF6B00]/15 text-[#FFB800] font-bold" : "border-white/10 text-zinc-400 hover:text-white"}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500">Source</label>
          <div className="mt-2 inline-flex p-1 rounded-full bg-zinc-900/70 border border-white/10 flex-wrap">
            <button type="button" data-testid="source-upload-btn" onClick={() => setSourceMode("upload")} className={`px-4 h-8 rounded-full text-xs uppercase tracking-widest ${sourceMode === "upload" ? "bg-[#FF6B00] text-black font-bold" : "text-zinc-400"}`}>Upload File</button>
            <button type="button" data-testid="source-url-btn" onClick={() => setSourceMode("url")} className={`px-4 h-8 rounded-full text-xs uppercase tracking-widest ${sourceMode === "url" ? "bg-[#FF6B00] text-black font-bold" : "text-zinc-400"}`}>External URL</button>
            <button type="button" data-testid="source-import-btn" onClick={() => setSourceMode("import")} className={`px-4 h-8 rounded-full text-xs uppercase tracking-widest inline-flex items-center gap-1 ${sourceMode === "import" ? "bg-[#FF6B00] text-black font-bold" : "text-zinc-400"}`}>
              <Wand2 className="w-3 h-3" /> Import
            </button>
          </div>
        </div>

        {sourceMode === "upload" && (
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Video <span className="text-[#FFB800]">or audio</span> file</label>
            <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={onDrop}
              className={`mt-2 border-2 border-dashed rounded-xl p-10 text-center transition-colors ${drag ? "border-[#FF6B00] bg-[#FF6B00]/10" : "border-[#FFB800]/40 bg-zinc-900/40"}`}>
              <Upload className="w-10 h-10 mx-auto text-[#FF6B00] aura-float" />
              <p className="mt-3 text-sm text-zinc-300">Drag &amp; drop a video <span className="text-[#FFB800]">or audio (.mp3/.wav/.flac)</span> file here</p>
              <p className="text-xs text-zinc-500">or</p>
              <label className="mt-2 inline-block">
                <input type="file" accept="video/*,audio/*,.mp3,.wav,.flac,.m4a,.aac,.ogg,.opus" data-testid="upload-video-file" onChange={(e) => pickFile(e.target.files?.[0])} className="hidden" />
                <span className="cursor-pointer inline-block px-5 h-10 leading-10 rounded-full bg-zinc-800 hover:bg-zinc-700 text-xs uppercase tracking-widest border border-white/10">Browse</span>
              </label>
              {videoFile && (
                <p className="mt-3 text-xs font-mono">
                  <span className="text-[#FFB800]">{videoFile.name}</span>
                  <span className="text-zinc-500"> · {(videoFile.size / 1024 / 1024).toFixed(1)} MB{isAudio(videoFile) ? " · 🎵 audio drop" : ""}</span>
                </p>
              )}
              {progress !== null && (
                <div className="mt-3 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                  <div className="h-full bg-[#FF6B00] transition-all" style={{ width: `${progress}%` }} />
                </div>
              )}
            </div>
          </div>
        )}
        {sourceMode === "url" && (
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Video URL (mp4 / webm / HLS)</label>
            <div className="mt-2 relative">
              <LinkIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input data-testid="upload-video-url" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="https://..."
                className="w-full pl-9 h-11 bg-zinc-900/70 border border-white/10 focus:border-[#FF6B00] outline-none rounded-lg text-white text-sm" />
            </div>
          </div>
        )}
        {sourceMode === "import" && (
          <div data-testid="import-panel">
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1.5">
              <Sparkles className="w-3 h-3 text-[#FFB800]" /> URL or Title
            </label>
            <div className="mt-2 flex items-stretch gap-2">
              <div className="relative flex-1">
                <LinkIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  data-testid="import-url"
                  value={importUrl}
                  onChange={(e) => { setImportUrl(e.target.value); setImportPreview(null); }}
                  placeholder="Paste a TikTok / Reel / Shorts URL, OR type a video title to YouTube-search"
                  className="w-full pl-9 h-11 bg-zinc-900/70 border border-white/10 focus:border-[#FF6B00] outline-none rounded-lg text-white text-sm font-mono"
                />
              </div>
              <button
                type="button"
                data-testid="import-fetch"
                onClick={fetchPreview}
                disabled={importing || !importUrl.trim()}
                className="px-5 rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-white/10 text-xs uppercase tracking-widest disabled:opacity-50 inline-flex items-center gap-1.5 text-white"
              >
                <Wand2 className="w-3.5 h-3.5" /> {importing ? "Fetching..." : "Fetch"}
              </button>
            </div>
            <p className="mt-2 text-[10px] text-zinc-500">Powered by yt-dlp · paste a direct URL (TikTok, Instagram Reels, YouTube Shorts, X, Reddit, Vimeo, 1500+ sites) <span className="text-[#FFB800]">OR just paste a title</span> — we&apos;ll YouTube-search and import the top result.</p>

            {importPreview && (
              <div data-testid="import-preview" className="mt-4 rounded-xl border border-[#FF6B00]/30 bg-[#FF6B00]/5 p-4 fade-up">
                <div className="flex gap-4">
                  {importPreview.thumbnail && (
                    <div className={`flex-shrink-0 ${importPreview.is_vertical ? "w-20 h-32" : "w-32 h-20"} rounded-lg overflow-hidden bg-black`}>
                      <img src={importPreview.thumbnail} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800] font-bold inline-flex items-center gap-1.5">
                      <Sparkles className="w-3 h-3" /> {importPreview.extractor || "Extracted"} · {importPreview.suggested_type === "short" ? "Short / POV" : "Long video"}
                    </span>
                    <p className="mt-1 text-sm text-white font-bold line-clamp-2">{importPreview.title}</p>
                    <p className="mt-1 text-xs text-zinc-500 line-clamp-2">{importPreview.description}</p>
                    <div className="mt-2 flex flex-wrap gap-3 text-[10px] uppercase tracking-widest text-zinc-500">
                      {importPreview.uploader && <span>By {importPreview.uploader}</span>}
                      {importPreview.duration && <span>{Math.floor(importPreview.duration / 60)}:{String(Math.floor(importPreview.duration % 60)).padStart(2, "0")}</span>}
                      {importPreview.width > 0 && <span>{importPreview.width}×{importPreview.height}</span>}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Thumbnail file (optional)</label>
            <input type="file" accept="image/*" data-testid="upload-thumb-file" onChange={(e) => setThumbFile(e.target.files?.[0])} className="mt-2 w-full text-xs file:mr-3 file:px-4 file:h-9 file:rounded-full file:border-0 file:bg-zinc-800 file:text-white" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Thumbnail URL (optional)</label>
            <input data-testid="upload-thumb-url" value={thumbUrl} onChange={(e) => setThumbUrl(e.target.value)} placeholder="https://..."
              className="mt-2 w-full h-11 px-3 bg-zinc-900/70 border border-white/10 focus:border-[#FF6B00] outline-none rounded-lg text-white text-sm" />
          </div>
        </div>
      </div>

      <div className="lg:col-span-5 rounded-2xl p-6 border border-white/5 bg-zinc-900/40 space-y-5">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500">Title</label>
          <input data-testid="upload-title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 w-full h-11 px-4 bg-zinc-900/70 border border-white/10 focus:border-[#FF6B00] outline-none rounded-lg text-white text-sm" required />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500">Description</label>
          <textarea data-testid="upload-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={5} className="mt-1 w-full px-4 py-2 bg-zinc-900/70 border border-white/10 focus:border-[#FF6B00] outline-none rounded-lg text-white text-sm" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Category</label>
            <div className="mt-1">
              <CategorySelect value={category} onChange={setCategory} testid="upload-category" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Tags (comma)</label>
            <input data-testid="upload-tags" value={tags} onChange={(e) => setTags(e.target.value)} className="mt-1 w-full h-11 px-4 bg-zinc-900/70 border border-white/10 focus:border-[#FF6B00] outline-none rounded-lg text-white text-sm" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Series (optional)</label>
            <input data-testid="upload-series" value={series} onChange={(e) => setSeries(e.target.value)} placeholder="e.g. Saiyan Tournament" className="mt-1 w-full h-11 px-4 bg-zinc-900/70 border border-white/10 focus:border-[#FF6B00] outline-none rounded-lg text-white text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Part #</label>
            <input data-testid="upload-series-order" type="number" min="1" value={seriesOrder} onChange={(e) => setSeriesOrder(e.target.value)} placeholder="1" className="mt-1 w-full h-11 px-4 bg-zinc-900/70 border border-white/10 focus:border-[#FF6B00] outline-none rounded-lg text-white text-sm" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-zinc-300">
          <input type="checkbox" data-testid="upload-featured" checked={featured} onChange={(e) => setFeatured(e.target.checked)} className="w-4 h-4 accent-[#FF6B00]" />
          Mark as Featured (Hero)
        </label>

        {/* Vanessa landing-page fields */}
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500">Landing slug (optional)</label>
          <input
            data-testid="upload-slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="kebab-case-from-title"
            className="mt-1 w-full h-11 px-4 bg-zinc-900/70 border border-white/10 focus:border-[#FF6B00] outline-none rounded-lg text-white text-xs font-mono"
          />
          <p className="text-[10px] text-zinc-500 mt-1">
            Becomes <code className="text-[#FFB800]">/v/&lt;slug&gt;</code>. Auto-generated from the title if blank. Max 80 chars, kebab-case.
          </p>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500">Music release (optional)</label>
          <input
            data-testid="upload-music-release"
            value={musicRelease}
            onChange={(e) => setMusicRelease(e.target.value)}
            placeholder="e.g. Saiyan Aura EP"
            className="mt-1 w-full h-11 px-4 bg-zinc-900/70 border border-white/10 focus:border-[#FF6B00] outline-none rounded-lg text-white text-sm"
          />
        </div>
        <MirrorsEditor
          value={mirrors}
          onChange={setMirrors}
          testidPrefix="upload-mirrors"
          autoDetectUrl={sourceMode === "import" ? importUrl : (sourceMode === "url" ? videoUrl : "")}
        />

        <button data-testid="upload-submit" disabled={busy} type="submit" className="aura-button w-full h-12 rounded-full text-sm uppercase tracking-wider disabled:opacity-50">
          {busy ? (sourceMode === "import" ? "Importing..." : "Transmitting...") : (sourceMode === "import" ? "Import to Nexus" : "Publish to Nexus")}
        </button>
      </div>
    </form>
  );
}

function EditModal({ video, onClose, onSaved }) {
  const [title, setTitle] = useState(video.title);
  const [description, setDescription] = useState(video.description || "");
  const [category, setCategory] = useState(video.category || "General");
  const [tags, setTags] = useState((video.tags || []).join(", "));
  const [series, setSeries] = useState(video.series || "");
  const [seriesOrder, setSeriesOrder] = useState(video.series_order || "");
  const [featured, setFeatured] = useState(!!video.is_featured);
  const [thumbPath, setThumbPath] = useState(video.thumbnail_path || "");
  const [thumbUrl, setThumbUrl] = useState(video.thumbnail_url || "");
  const [thumbUploading, setThumbUploading] = useState(false);
  const [thumbPreview, setThumbPreview] = useState(video.thumbnail || "");
  const [pickingFrame, setPickingFrame] = useState(false);
  const [ageRating, setAgeRating] = useState(video.age_rating || "G");
  // Vanessa landing-page fields
  const [slug, setSlug] = useState(video.slug || "");
  const [mirrors, setMirrors] = useState(video.mirrors || {});
  const [musicRelease, setMusicRelease] = useState(video.music_release || "");
  const [regenerating, setRegenerating] = useState(false);

  const onPickThumb = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setThumbUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kind", "thumbnail");
      const r = await client.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setThumbPath(r.data.path);
      setThumbUrl(""); // local takes precedence
      setThumbPreview(`/api/files/${r.data.path}`);
      toast.success("Thumbnail uploaded — click Save to apply.");
    } catch {
      toast.error("Thumbnail upload failed");
    } finally {
      setThumbUploading(false);
    }
  };

  const onFrameCaptured = async (blob) => {
    setThumbUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "frame.jpg");
      fd.append("kind", "thumbnail");
      const r = await client.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setThumbPath(r.data.path);
      setThumbUrl("");
      setThumbPreview(`/api/files/${r.data.path}`);
      setPickingFrame(false);
      toast.success("Frame captured — click Save to apply.");
    } catch {
      toast.error("Frame upload failed");
    } finally {
      setThumbUploading(false);
    }
  };

  const save = async () => {
    try {
      await client.put(`/admin/videos/${video.id}`, {
        title, description, category,
        tags: tags.split(",").map((s) => s.trim()).filter(Boolean),
        is_featured: featured,
        series: series.trim() || null,
        series_order: seriesOrder ? parseInt(seriesOrder, 10) : null,
        thumbnail_path: thumbPath || null,
        thumbnail_url: thumbUrl.trim() || null,
        slug: slug.trim() || null,
        mirrors,
        music_release: musicRelease.trim() || null,
        age_rating: ageRating,
      });
      toast.success("Saved.");
      onSaved();
    } catch { toast.error("Save failed"); }
  };

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const r = await client.post(`/admin/videos/${video.id}/regenerate-landing`);
      const wh = r.data?.webhook || {};
      const rg = r.data?.regenerate || {};
      if (wh.sent && rg.status && rg.status < 400) {
        toast.success(`Landing page rebuilt · /v/${r.data.slug}`);
      } else if (wh.skipped) {
        toast.error(`Webhook skipped: ${wh.reason || "missing secret"}`);
      } else if (rg.reason) {
        toast.warning(`Webhook fired but regenerate skipped: ${rg.reason}`);
      } else {
        toast.warning(`Webhook status=${wh.status || "?"} · regenerate status=${rg.status || "?"}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Regenerate failed");
    } finally {
      setRegenerating(false);
    }
  };

  const landingUrl = video.slug ? `https://nexusplayer.thesaiyanvictoria.com/v/${video.slug}` : null;
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-4" onClick={onClose} data-testid="edit-modal">
      <div onClick={(e) => e.stopPropagation()} className="rounded-2xl bg-[#0a0a0a] border border-white/10 max-w-lg w-full p-6 fade-up max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">Edit Video</h3>
          <button data-testid="edit-close" onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="mt-5 space-y-4">
          <input data-testid="edit-title" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full h-11 px-4 bg-zinc-900/70 border border-white/10 rounded-lg text-white text-sm" placeholder="Title" />
          <textarea data-testid="edit-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={4} className="w-full px-4 py-2 bg-zinc-900/70 border border-white/10 rounded-lg text-white text-sm" placeholder="Description" />
          <CategorySelect value={category} onChange={setCategory} testid="edit-category" />
          <input data-testid="edit-tags" value={tags} onChange={(e) => setTags(e.target.value)} className="w-full h-11 px-4 bg-zinc-900/70 border border-white/10 rounded-lg text-white text-sm" placeholder="Tags (comma)" />

          {/* Thumbnail editor */}
          <div className="rounded-xl border border-white/10 p-3 space-y-3 bg-black/30">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-widest text-[#FFB800]">Thumbnail</span>
              {thumbPath && (
                <button
                  type="button"
                  data-testid="edit-thumb-clear"
                  onClick={() => { setThumbPath(""); setThumbPreview(video.thumbnail_url || ""); }}
                  className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-red-400"
                >
                  ↻ Use original
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <div className="w-24 h-14 rounded-lg bg-zinc-800 overflow-hidden flex-shrink-0 border border-white/5">
                {thumbPreview && <img src={resolveMedia(thumbPreview)} alt="" className="w-full h-full object-cover" data-testid="edit-thumb-preview" />}
              </div>
              <div className="flex-1 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <label className="block">
                    <input data-testid="edit-thumb-file" type="file" accept="image/*" onChange={onPickThumb} className="hidden" />
                    <span className={`inline-block px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-white/10 text-[10px] uppercase tracking-widest text-white cursor-pointer ${thumbUploading ? "opacity-50" : ""}`}>
                      {thumbUploading ? "Working…" : "📷 Upload"}
                    </span>
                  </label>
                  <button
                    type="button"
                    data-testid="edit-thumb-pick-frame"
                    onClick={() => setPickingFrame(true)}
                    disabled={!video.playback_url || thumbUploading}
                    className="px-3 py-2 rounded-lg bg-[#FF6B00] hover:brightness-110 text-black text-[10px] uppercase tracking-widest font-black disabled:opacity-40"
                  >
                    🎞 Pick from video
                  </button>
                </div>
                <input
                  data-testid="edit-thumb-url"
                  value={thumbUrl}
                  onChange={(e) => { setThumbUrl(e.target.value); if (e.target.value) { setThumbPath(""); setThumbPreview(e.target.value); } }}
                  className="w-full h-9 px-3 bg-zinc-900/70 border border-white/10 rounded-lg text-white text-xs font-mono"
                  placeholder="…or paste image URL"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <input data-testid="edit-series" value={series} onChange={(e) => setSeries(e.target.value)} className="col-span-2 h-11 px-4 bg-zinc-900/70 border border-white/10 rounded-lg text-white text-sm" placeholder="Series name" />
            <input data-testid="edit-series-order" type="number" min="1" value={seriesOrder} onChange={(e) => setSeriesOrder(e.target.value)} className="h-11 px-4 bg-zinc-900/70 border border-white/10 rounded-lg text-white text-sm" placeholder="Part #" />
          </div>
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            <input type="checkbox" data-testid="edit-featured" checked={featured} onChange={(e) => setFeatured(e.target.checked)} className="w-4 h-4 accent-[#FF6B00]" />
            Featured
          </label>

          {/* Age rating */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Age Rating</label>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {["G", "PG", "PG-13", "R", "Explicit"].map((r) => {
                const isGated = r === "R" || r === "Explicit";
                const selected = ageRating === r;
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setAgeRating(r)}
                    data-testid={`edit-rating-${r}`}
                    className={`h-9 px-3 rounded-full text-xs font-bold uppercase tracking-widest border transition-colors ${
                      selected
                        ? isGated
                          ? "bg-red-500/20 text-red-300 border-red-500/60"
                          : "bg-[#FF6B00] text-black border-[#FF6B00]"
                        : "bg-zinc-900/70 text-zinc-400 hover:text-white border-white/10"
                    }`}
                  >
                    {r}
                  </button>
                );
              })}
            </div>
            <p className="mt-1.5 text-[10px] text-zinc-500">
              R / Explicit videos are hidden from public feeds & sitemap and require birth-year verification before playback.
            </p>
          </div>

          {/* Vanessa landing-page fields */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Landing slug</label>
            <input
              data-testid="edit-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="kebab-case-from-title"
              className="mt-1 w-full h-10 px-3 bg-zinc-900/70 border border-white/10 focus:border-[#FF6B00] outline-none rounded-lg text-white text-xs font-mono"
            />
            {landingUrl && (
              <a
                data-testid="edit-view-landing"
                href={landingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 inline-block text-[10px] uppercase tracking-widest text-[#FFB800] hover:text-white"
              >
                ↗ View landing page · /v/{video.slug}
              </a>
            )}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Music release (optional)</label>
            <input
              data-testid="edit-music-release"
              value={musicRelease}
              onChange={(e) => setMusicRelease(e.target.value)}
              placeholder="e.g. Saiyan Aura EP"
              className="mt-1 w-full h-10 px-3 bg-zinc-900/70 border border-white/10 focus:border-[#FF6B00] outline-none rounded-lg text-white text-sm"
            />
          </div>
          <MirrorsEditor
            value={mirrors}
            onChange={setMirrors}
            testidPrefix="edit-mirrors"
            autoDetectUrl={video.external_url || ""}
          />
        </div>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button data-testid="edit-save" onClick={save} className="aura-button h-11 rounded-full text-sm uppercase tracking-wider">Save Changes</button>
          <button
            data-testid="edit-regenerate-landing"
            onClick={regenerate}
            disabled={regenerating}
            className="h-11 rounded-full bg-zinc-800 hover:bg-zinc-700 border border-[#FFB800]/40 text-[#FFB800] text-[11px] uppercase tracking-widest font-black disabled:opacity-50"
          >
            {regenerating ? "Rebuilding…" : "↻ Rebuild landing page"}
          </button>
        </div>
      </div>
      {pickingFrame && (
        <FramePicker
          src={resolveMedia(video.playback_url)}
          onCancel={() => setPickingFrame(false)}
          onCapture={onFrameCaptured}
          busy={thumbUploading}
        />
      )}
    </div>
  );
}

function FramePicker({ src, onCancel, onCapture, busy }) {
  const vidRef = useRef(null);
  const canvasRef = useRef(null);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [previewUrl, setPreviewUrl] = useState("");
  const [capturing, setCapturing] = useState(false);

  useEffect(() => {
    const v = vidRef.current;
    if (!v) return;
    const onMeta = () => setDuration(v.duration || 0);
    const onTime = () => setCurrent(v.currentTime || 0);
    v.addEventListener("loadedmetadata", onMeta);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("seeked", onTime);
    return () => {
      v.removeEventListener("loadedmetadata", onMeta);
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("seeked", onTime);
    };
  }, []);

  const seek = (t) => {
    const v = vidRef.current;
    if (!v) return;
    v.pause();
    v.currentTime = Math.max(0, Math.min(t, duration || t));
  };

  const captureCurrent = () => {
    const v = vidRef.current;
    if (!v || !canvasRef.current) return;
    setCapturing(true);
    const w = v.videoWidth;
    const h = v.videoHeight;
    if (!w || !h) {
      setCapturing(false);
      toast.error("Video not ready yet");
      return;
    }
    canvasRef.current.width = w;
    canvasRef.current.height = h;
    const ctx = canvasRef.current.getContext("2d");
    try {
      ctx.drawImage(v, 0, 0, w, h);
      const url = canvasRef.current.toDataURL("image/jpeg", 0.9);
      setPreviewUrl(url);
    } catch (e) {
      toast.error("Capture blocked (CORS) — try Upload instead");
    }
    setCapturing(false);
  };

  const useThisFrame = async () => {
    if (!canvasRef.current) return;
    canvasRef.current.toBlob((blob) => { if (blob) onCapture(blob); }, "image/jpeg", 0.9);
  };

  const fmt = (s) => {
    if (!isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${String(sec).padStart(2, "0")}`;
  };

  return (
    <div
      data-testid="frame-picker"
      className="fixed inset-0 z-[60] bg-black/95 backdrop-blur flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div onClick={(e) => e.stopPropagation()} className="rounded-2xl bg-[#0a0a0a] border border-white/10 max-w-2xl w-full p-5 fade-up max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-white">🎞 Pick a Frame</h3>
          <button data-testid="frame-picker-close" onClick={onCancel} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-zinc-500 mt-1">Scrub the video, then click <span className="text-[#FFB800]">Capture</span> to freeze that exact frame.</p>

        <div className="mt-4 rounded-lg overflow-hidden bg-black aspect-video relative">
          <video
            ref={vidRef}
            src={src}
            crossOrigin="anonymous"
            preload="auto"
            playsInline
            controls
            className="w-full h-full object-contain"
            data-testid="frame-picker-video"
          >
            <track kind="captions" />
          </video>
        </div>

        <div className="mt-3 space-y-2">
          <input
            data-testid="frame-picker-scrub"
            type="range"
            min={0}
            max={duration || 0}
            step={0.1}
            value={current}
            onChange={(e) => seek(parseFloat(e.target.value))}
            className="w-full accent-[#FF6B00]"
          />
          <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
            <span data-testid="frame-picker-time">{fmt(current)} / {fmt(duration)}</span>
            <div className="flex gap-1">
              <button onClick={() => seek(current - 1/30)} className="px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-white" title="-1 frame">«</button>
              <button onClick={() => seek(current - 1)} className="px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-white">-1s</button>
              <button onClick={() => seek(current + 1)} className="px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-white">+1s</button>
              <button onClick={() => seek(current + 1/30)} className="px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-white" title="+1 frame">»</button>
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            type="button"
            data-testid="frame-picker-capture"
            onClick={captureCurrent}
            disabled={capturing}
            className="px-4 py-3 rounded-lg bg-[#FF6B00] text-black text-[11px] font-black uppercase tracking-widest hover:brightness-110 disabled:opacity-50"
          >
            {capturing ? "Capturing…" : "📸 Capture frame"}
          </button>
          <button
            type="button"
            data-testid="frame-picker-use"
            onClick={useThisFrame}
            disabled={!previewUrl || busy}
            className="px-4 py-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-white/10 text-white text-[11px] font-black uppercase tracking-widest disabled:opacity-40"
          >
            {busy ? "Saving…" : "✓ Use this frame"}
          </button>
        </div>

        {previewUrl && (
          <div className="mt-4 rounded-lg overflow-hidden border border-[#FF6B00]/30">
            <img data-testid="frame-picker-preview" src={previewUrl} alt="Captured frame" className="w-full" />
          </div>
        )}
        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>
  );
}

/* ---------- Root ---------- */
export default function AdminDashboard() {
  const { admin, loading } = useAuth();
  if (loading) return <div className="p-12 text-zinc-500" data-testid="admin-loading">Authenticating...</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;
  return (
    <StudioShell>
      <Routes>
        <Route index element={<Dashboard />} />
        <Route path="content" element={<ContentPage />} />
        <Route path="sources" element={<SourcesPage />} />
        <Route path="analytics" element={<AnalyticsTabbed><AnalyticsLanding hideHeader /></AnalyticsTabbed>} />
        <Route path="analytics/:id" element={<VideoAnalytics />} />
        <Route path="victoria" element={<VictoriaPage />} />
        <Route path="upload" element={<UploadPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Routes>
      <VictoriaBubble />
    </StudioShell>
  );
}
